import { PairedFile, sourceFilesByUrlAtom } from './atoms'
import React, { useMemo } from 'react'
import { IngestedPreviewButton } from '../../components/ingestion/IngestedPreviewButton'
import { useDownloadSource, useUpdatePreview } from './hooks'
import { Box, Button } from '@mui/material'
import { formatDistanceToNow } from 'date-fns'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { Checkmark } from '../IMSpanish/components/Checkmark'
import { copyToClipboard } from '../../utility'
import { CreateTreeButton } from '../../components/ingestion/CreateTreeButton'
import { SortableHeader } from '../../components/utility/SortableHeader'
import { parseCourse } from './ingestion/course'

const courseValue = ({ source: { key } }: PairedFile) => {
  return key.split('_').pop()!.split('.')[0]
}
const curriculaValue = ({ source: { key } }: PairedFile) => {
  return key.split('_')[1]
}

export function useTableColumns() {
  const doUpdate = useUpdatePreview()
  const doDownload = useDownloadSource()
  const sourceFiles = useAtomValue(sourceFilesByUrlAtom)
  return useMemo(() => {
    const colValMap = {
      course: courseValue,
      curricula: curriculaValue,
      lastModified: (item: PairedFile) => {
        return item.source.last_modified
      },
    }
    const columns = [
      {
        title: <strong>Grade Band</strong>,
        width: 50,
        rowContent({ source: { key } }: PairedFile) {
          return (
            <Button onClick={() => copyToClipboard(key)}>
              {key.split('_')[0]}
            </Button>
          )
        },
      },
      {
        title: <SortableHeader title={'Curricula'} id={'curricula'} />,
        width: 75,
        rowContent: curriculaValue,
      },
      {
        title: <SortableHeader title={'Course'} id={'course'} />,
        width: 100,
        rowContent: courseValue,
      },
      {
        title: <strong>Slug</strong>,
        width: 50,
        rowContent(item: PairedFile) {
          const file = sourceFiles[item.source.URL]
          if (!file) {
            return <Button onClick={() => doDownload(item)}>Fetch</Button>
          }
          return file.slug
        },
      },
      {
        title: <SortableHeader title={'Last Modified'} id={'lastModified'} />,
        width: 100,
        rowContent(item: PairedFile) {
          return new Date(item.source.last_modified).toLocaleDateString('en-US')
        },
      },
      {
        title: <strong>Released</strong>,
        width: 50,
        rowContent(item: PairedFile) {
          const file = sourceFiles[item.source.URL]
          if (!file) {
            return <Button onClick={() => doDownload(item)}>Check</Button>
          }
          const parseButton = (
            <Box>
              <Button onClick={() => parseCourse(file)}>ParseTest</Button>
            </Box>
          )
          if (file.released) {
            return (
              <>
                <Box>
                  <Checkmark />
                </Box>
                {parseButton}
              </>
            )
          }
          return (
            <>
              <Box>X</Box>
              {parseButton}
            </>
          )
        },
      },
      {
        title: <strong>Ingested</strong>,
        width: 100,
        rowContent(item: PairedFile) {
          const { ingested } = item
          if (!ingested) {
            return (
              <Button onClick={() => doUpdate(item)}>Create Preview</Button>
            )
          }
          return <Button onClick={() => doUpdate(item)}>Update Preview</Button>
        },
      },
      {
        title: <strong>Preview Link</strong>,
        width: 200,
        rowContent(item: PairedFile) {
          const { ingested } = item
          if (!ingested) {
            return (
              <Button onClick={() => doUpdate(item)}>Create Preview</Button>
            )
          }
          return (
            <Box>
              <IngestedPreviewButton ingestionKey={ingested.key} />
              {`${formatDistanceToNow(new Date(ingested.last_modified))} ago`}
            </Box>
          )
        },
      },
      {
        title: <strong>Create Tree</strong>,
        width: 200,
        rowContent(item: PairedFile) {
          const { ingested } = item
          if (!ingested) {
            return (
              <Button onClick={() => doUpdate(item)}>Create Preview</Button>
            )
          }
          return (
            <Box>
              <CreateTreeButton ingestionKey={ingested.key} />
            </Box>
          )
        },
      },
    ]
    return { columns, colValMap }
  }, [sourceFiles, doUpdate, doDownload])
}
